// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }
  // return 'http://localhost:4000';

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// A dedicated request function for handling FormData
const requestFormData = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;

  // For FormData requests, leave headers empty so that the browser sets multipart boundaries.
  // Also, do not perform any serialization on the body.
  const fetchOptions = {
    credentials: options.credentials || 'include',
    headers: options.headers || {}, // leave headers empty
    ...options,
  };

  return fetch(url, fetchOptions).then(response => response.json());
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
export const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

/**
 * Makes a POST request to the local API endpoint with a FormData body.
 *
 * @param {string} path - the path of the API endpoint
 * @param {FormData} formData - the form data to send with the request
 * @param {Object} [options] - additional options to pass to the request
 * @returns {Promise} resolves to the response of the request
 */
const postFormData = (path, formData, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body: formData,
  };
  return requestFormData(path, requestOptions);
};

export const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const fetchTrustedSuppliers = body => {
  return post('/api/strapi/fetch-trusted-suppliers', body);
};

export const fetchSupplierTypes = body => {
  return get('/api/strapi/fetch-supplier-types', body);
};

export const fetchBlogs = body => {
  return post('/api/strapi/fetch-blogs', body);
};

export const fetchBlog = body => {
  return post('/api/strapi/fetch-blog', body);
};

export const fetchTagsFilter = body => {
  return post('/api/strapi/fetch-blogs-tags', body);
};

export const fetchGeographicalCoverage = body => {
  return get('/api/strapi/fetch-geographical-coverage', body);
};

export const fetchFaqs = body => {
  return post('/api/strapi/fetch-faqs', body);
};

// Firebase
/**
 * Firebase API methods
 *
 * @typedef {Object} FirebaseApi
 * @property {function} uploadFileToFirebase - Upload file to Firebase
 * @property {function} deleteFileFromFirebase - Delete file from Firebase
 * @property {function} downloadZipFromFirebase - Download zip from Firebase
 */
export const FirebaseApi = {
  /**
   * Upload file to Firebase
   *
   * @param {Object} body - Request body
   * @param {File} body.file - File to upload
   * @param {string} body.fileName - File name
   * @param {string} [body.folderName] - Folder name
   * @param {Object} [body.metadata] - File metadata
   * @returns {Promise} Server response
   */
  uploadFileToFirebase: body => {
    return postFormData('/api/upload-file', body);
  },

  /**
   * Delete file from Firebase
   *
   * @param {Object} body - Request body
   * @param {string} body.fileName - File name
   * @param {string} [body.folderName] - Folder name
   * @returns {Promise} Server response
   */
  deleteFileFromFirebase: body => {
    return post('/api/delete-file', body);
  },

  /**
   * Download zip from Firebase
   *
   * @param {Object} body - Request body
   * @param {Array<string>} body.fileNames - File names
   * @param {string} [body.folderName] - Folder name
   * @returns {Promise} Server response
   */
  downloadZipFromFirebase: body => {
    return post('/api/download-zip', body);
  },
  /**
   * Fetch cost estimations from Firebase
   *
   * @param {Object} body - Request body
   * @param {string} body.userId - User ID
   * @param {string} [body.listingId] - Listing ID
   * @returns {Promise} Server response
   */
  fetchCostEstimationsDocuments: body => {
    return post('/api/fetch-cost-estimations', body);
  },

  saveCostEstimation: body => {
    return post('/api/save-cost-estimations', body);
  },
};

/**
 * Sendgrid API methods
 *
 * @typedef {Object} SendgridApi
 * @property {function} sendGridNotification - Send notification email
 */
export const SendgridApi = {
  /**
   * Send notification email
   *
   * @param {Object} body - Request body
   * @param {string} body.subject - Email subject
   * @param {string} body.html - Email HTML content
   * @param {boolean} [body.forAdmin] - Whether to send the email to the admin
   * @param {string} [body.email] - Email address of the recipient
   * @returns {Promise} Server response
   */
  sendGridNotification: body => {
    return post('/api/send-notification', body);
  },

  /**
   * Submit the public landing page contact form (sent to the admin inbox)
   *
   * @param {Object} body - Request body
   * @param {string} body.fName - First name
   * @param {string} body.lName - Last name
   * @param {string} body.email - Sender email
   * @param {string} body.message - Message
   * @param {boolean} body.terms - Privacy policy accepted
   * @returns {Promise} Server response
   */
  submitContactForm: body => {
    return post('/api/contact-form/submit', body);
  },
};

/**
 * Sharetribe API methods
 *
 * @typedef {Object} SharetribeApi
 * @property {function} fetchSuppliersAndNotify - Fetch suppliers and notify them
 * @property {function} updateListingThroughIsdk - Update listing through ISDK
 */
export const SharetribeApi = {
  /**
   * Fetch suppliers and notify them
   *
   * @param {Object} body - Request body
   * @param {string} body.projectUrl - URL of the project
   * @param {string} body.documentsUrl - URL of the project documents
   * @param {string} body.estimationToolUrl - URL of the estimation tool
   * @param {string} [body.listingId] - ID of the listing
   * @returns {Promise} Server response
   */
  fetchSuppliersAndNotify: body => {
    return post('/api/fetch-suppliers-and-notify', body);
  },

  /**
   * Fetch the author of a listing and notify them
   *
   * @param {Object} body - Request body
   * @param {string} body.listingAuthorId - ID of the listing author
   * @param {string} [body.supplierId] - ID of the supplier
   * @returns {Promise} Server response
   */
  fetchListingAuthorAndNotify: body => {
    return post('/api/fetch-author-and-notify', body);
  },

  /**
   * Update listing through ISDK
   *
   * @param {Object} body - Request body
   * @param {string} body.listingId - ID of the listing
   * @param {Object} body.data - Listing data
   * @returns {Promise} Server response
   */
  updateListingThroughIsdk: body => {
    return post('/api/update-listing', body);
  },
};
